import type { ValueByCulture } from '~/types/root'

/**
 * Contains the available stage
 *
 * @enum {number}
 */
export enum Env {
  Local = 'local',
  Development = 'development',
  Testing = 'testing',
  Release = 'release',
  E2E = 'e2e'
}

/**
 * Contains the available stage
 *
 * @enum {number}
 */
export enum Stage {
  dev = 'dev',
  qa = 'qa',
  prod = 'prod',
}

export enum Protocol {
  HTTP = 'http://',
  HTTPS = 'https://'
}

// todo separate into runtime and build time
export const EnvironmentKeysPublic = [
  'ENVIRONMENT',
  'API_PREFIX',
  'GTM_ID',
  'RECAPTCHA_KEY',
  'MAX_SAVED_CARDS',
  'RECURLY_KEY',
  'RECURLYJS_URL',
  'SIGNIFYD_SCRIPT_URL',
  'CHAT_URL',
  'CHAT_FLOW_ID',
  'CHAT_REGION',
  'MEDIA_ASSET_URL',
  'CF_RESIZE_ZONE',
  'CF_IMAGE_ORIGIN',
  'ENABLED_FEATURES',
  'HYBRID_ROUTES',
  'API_PORT',
  'HTTP_PORT',
  'CLARITY_PROJECT_ID',
  'OSANO_JS_URL',
]
export const EnvironmentKeysPrivate = [
  'BACKEND_SERVER_URL',
  'CONFIGCAT_KEY',
  'CONFIGCAT_CACHE_TIMEOUT'
]

declare module 'nuxt/schema' {
  interface RuntimeConfig {
    /**
     * CONFIGCAT KEY
     *
     * @type {string}
     * @memberof Environment
     */
    CONFIGCAT_KEY: string
    /**
     * CONFIGCAT cache timeout
     *
     * @type {string}
     * @memberof Environment
     */
    CONFIGCAT_CACHE_TIMEOUT: string
        /**
     * Backend url that is used when calling from server
     *
     * @type {number}
     * @memberof Environment
     */
    BACKEND_SERVER_URL: string

  }
  interface PublicRuntimeConfig {
       /**
     * Shows the current running environment
     *
     * @type {string}
     * @memberof Environment
     */
    ENVIRONMENT?: Env


        /**
     * Recaptcha key
     *
     * @type {number}
     * @memberof Environment
     */
    RECAPTCHA_KEY?: string

    /**
     * API domain prefix e.g. qa-api
     *
     * @type {string}
     * @memberof Environment
     */
    API_PREFIX: string
    /**
     * The media host. Serves transform-free media assets directly (ADO 62976),
     * and is the source origin that transformed image URLs are composed from.
     *
     * @type {string}
     * @memberof Environment
     */
    MEDIA_ASSET_URL?: string

    /**
     * Zone serving Cloudflare Image Resizing, used as the `/cdn-cgi/image/`
     * transform endpoint.
     *
     * @type {string}
     * @memberof Environment
     */
    CF_RESIZE_ZONE?: string

    /**
     * Product-image library path base, joined onto MEDIA_ASSET_URL to form the
     * transform source. A path rather than an absolute URL so the media host
     * has a single source of truth and cannot drift between the product-image
     * and media-asset paths (ADO 62975).
     *
     * @type {string}
     * @memberof Environment
     */
    CF_IMAGE_ORIGIN?: string
        /**
     * Region for chat script
     *
     * @type {string}
     * @memberof Environment
     */
    CHAT_REGION?: string
        /**
     * Flow ID for talk desk chat script
     *
     * @type {Array}
     * @memberof Environment
     */
    CHAT_FLOW_ID?: Array<ValueByCulture>
        /**
     * URL for Ring Central / In Contact chat script
     *
     * @type {string}
     * @memberof Environment
     */
    CHAT_URL?: string

    /**
     * Max number of credit cards a user can store
     *
     * @type {number}
     * @memberof Environment
     */
    MAX_SAVED_CARDS?: number
       /**
     * GTM ID
     *
     * @type {string}
     * @memberof Environment
     */
    GTM_ID: string

    /**
     * Osano cookie-consent script URL
     *
     * @type {string}
     * @memberof Environment
     */
    OSANO_JS_URL?: string

    /**
     * Enabled Features
     *
     * @type {string}
     * @memberof Environment
     */
    ENABLED_FEATURES: string
        /**
     * Public API Key for recurlyjs script
     *
     * @type {string}
     * @memberof Environment
     */
    RECURLY_KEY?: string
    /**
     * URL for recurlyjs script
     *
     * @type {string}
     * @memberof Environment
     */
    RECURLYJS_URL?: string
    /**
     * URL for the Signifyd device-fingerprinting script
     *
     * @type {string}
     * @memberof Environment
     */
    SIGNIFYD_SCRIPT_URL?: string
    /**
     * Hybrid routes (comma-separated) handled by Nuxt 4
     *
     * @type {string}
     * @memberof Environment
     */
    HYBRID_ROUTES?: string
    /**
     * API port for local backend (e.g. 5001)
     *
     * @type {string}
     * @memberof Environment
     */
    API_PORT?: string
    /**
     * Port the site is served on locally; appended to base URLs in the Local environment
     *
     * @type {string}
     * @memberof Environment
     */
    HTTP_PORT?: string
    /**
     * Microsoft Clarity project id, used to build session recording URLs
     *
     * @type {string}
     * @memberof Environment
     */
    CLARITY_PROJECT_ID?: string
  }
}
